import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Anchor, Award, Search, Sparkles } from "lucide-react";
import Logo from "../components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLE = {
  bronze: {
    label_no: "Ny Kaptein",
    label_en: "New Captain",
    gradient: "from-[#B87333] via-[#D9975B] to-[#F1BB7B]",
    Icon: Anchor,
  },
  silver: {
    label_no: "Kaptein",
    label_en: "Captain",
    gradient: "from-[#95A5B4] via-[#B9C5D0] to-[#E1E7EC]",
    Icon: Award,
  },
  gold: {
    label_no: "Storkaptein",
    label_en: "Grand Captain",
    gradient: "from-[#BD984D] via-[#D9BB6D] to-[#F1DA96]",
    Icon: Sparkles,
  },
};

/**
 * Public captain code lookup. Anyone can enter a code (usually the customer
 * showing off to friends) and see the captain's rank + perks. No login.
 */
export default function CaptainStatusPublic() {
  const [params, setParams] = useSearchParams();
  const initial = (params.get("code") || "").toUpperCase();
  const initialLang = (params.get("lang") || "en").toLowerCase() === "no" ? "no" : "en";
  const [lang, setLang] = useState(initialLang);
  const [code, setCode] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const T = (no, en) => (lang === "no" ? no : en);

  const lookup = async (raw) => {
    const c = (raw || code).trim().toUpperCase();
    if (!c) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/public/captain-status`,
        { params: { code: c } },
      );
      setResult(r.data);
      // Reflect in URL so it can be shared.
      setParams((p) => {
        const n = new URLSearchParams(p);
        n.set("code", c);
        return n;
      }, { replace: true });
    } catch (e) {
      setError(
        e.response?.status === 404
          ? T("Vi finner ikke denne koden.", "We can't find that code.")
          : T("Noe gikk galt. Prøv igjen.", "Something went wrong. Try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initial) lookup(initial);
  }, []);

  const tier = result ? TIER_STYLE[result.tier_key] : null;
  const TierIcon = tier?.Icon || Anchor;

  return (
    <div className="min-h-screen bg-[#0C1824] text-white flex flex-col">
      <header className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
        <Logo dark size={32} />
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
          <button
            onClick={() => setLang("no")}
            data-testid="captain-lang-no"
            className={`px-2 py-1 rounded ${lang === "no" ? "bg-[#BD984D] text-[#0C1824]" : "text-white/60 hover:text-white"}`}
          >
            NO
          </button>
          <button
            onClick={() => setLang("en")}
            data-testid="captain-lang-en"
            className={`px-2 py-1 rounded ${lang === "en" ? "bg-[#BD984D] text-[#0C1824]" : "text-white/60 hover:text-white"}`}
          >
            EN
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10 sm:py-16">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#ECC974] font-semibold">
          Canal Boats Telemark
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold mt-2">
          {T("Sjekk din kapteinstatus", "Check your captain status")}
        </h1>
        <p className="text-white/70 mt-3 max-w-xl">
          {T(
            "Skriv inn koden du fikk etter siste tur for å se rangen din og hvor mye rabatt du får på neste booking.",
            "Enter the code you received after your last trip to see your rank and how much you save on your next booking.",
          )}
        </p>

        <div className="mt-8 flex gap-2 flex-wrap">
          <input
            data-testid="captain-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") lookup(); }}
            placeholder="CBT-KP-XXXXX"
            className="flex-1 min-w-[200px] rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white text-lg font-mono tracking-widest placeholder:text-white/30 focus:outline-none focus:border-[#BD984D]"
          />
          <button
            onClick={() => lookup()}
            disabled={loading}
            data-testid="captain-code-lookup"
            className="rounded-xl bg-[#BD984D] hover:bg-[#A57F39] text-[#0C1824] font-bold px-6 py-3 text-lg inline-flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {loading ? T("Sjekker…", "Checking…") : T("Sjekk", "Check")}
          </button>
        </div>

        {error && (
          <div
            data-testid="captain-error"
            className="mt-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-200"
          >
            {error}
          </div>
        )}

        {result && tier && (
          <div className="mt-8" data-testid="captain-result">
            <div
              className={`rounded-3xl p-8 sm:p-10 border-2 border-white/20 bg-gradient-to-br ${tier.gradient} text-[#0C1824]`}
            >
              <div className="flex items-start gap-5 flex-wrap">
                <div className="h-20 w-20 rounded-full bg-white/60 backdrop-blur flex items-center justify-center border-4 border-white/70 shadow-xl">
                  <TierIcon className="w-10 h-10 text-[#0C1824]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.32em] font-bold opacity-80">
                    {result.captain_first_name
                      ? `${result.captain_first_name} · ${T("Kaptein", "Captain")}`
                      : T("Bekreftet kaptein", "Confirmed captain")}
                  </div>
                  <div className="text-4xl sm:text-5xl font-bold mt-1">
                    {lang === "no" ? result.tier_label_no : result.tier_label_en}
                  </div>
                  <div className="mt-2 text-sm font-semibold">
                    {result.trip_count} {T("fullførte turer", "completed trips")} · {result.discount_pct}% {T("rabatt", "off")}
                  </div>
                </div>
              </div>

              <ul className="mt-6 space-y-2">
                {(lang === "no" ? result.perks_no : result.perks_en).map((p, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="w-5 h-5 rounded-full bg-white/70 flex items-center justify-center shrink-0 mt-0.5 text-xs font-bold">
                      ✓
                    </span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>

              {result.is_redeemed && (
                <div className="mt-6 rounded-xl bg-black/20 text-white px-4 py-3 text-sm">
                  {T(
                    "Denne koden er allerede brukt. Ta en tur til for å låse opp en ny!",
                    "This code has already been used. Book another trip to unlock a new one!",
                  )}
                </div>
              )}
              {result.is_expired && (
                <div className="mt-3 rounded-xl bg-black/20 text-white px-4 py-3 text-sm">
                  {T("Koden er dessverre utløpt.", "This code has expired.")}
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/availability"
                data-testid="captain-book-cta"
                className="rounded-xl bg-[#BD984D] hover:bg-[#A57F39] text-[#0C1824] font-bold px-5 py-3 inline-flex items-center gap-2"
              >
                {T("Se ledige båter", "See available boats")}
              </Link>
              <Link
                to="/login"
                className="rounded-xl border border-white/20 text-white hover:bg-white/10 px-5 py-3"
              >
                {T("Logg inn på min side", "Sign in to my portal")}
              </Link>
            </div>
          </div>
        )}
      </main>

      <footer className="px-6 py-6 text-xs text-white/40 text-center">
        © Canal Boats Telemark
      </footer>
    </div>
  );
}
